import { useMemo } from 'react';
import { CategoryData, ChartTheme } from '../types';
import { useColor } from './useColor';
import { SortingMethod, getSortingMethod } from '../utils';
import { Theme } from '@redsift/design-system';

export interface UseFormatCategoricalDataProps {
  /** Dataset to use to generate the chart, should be a list of objects containing a key and a value. */
  data: CategoryData;
  /** Color palette to use. */
  colorTheme: ChartTheme;
  /** Define how to sort categories. */
  sortingMethod: SortingMethod;
  /** Number of categories to use, the rest being put into a new category called "Others". */
  caping?: number;
  /** Whether a category should be displayed for categories that has been removed by the caping option. Optionaly, this prop can be used to change the label of this category. */
  others?: boolean | string;
  /** Theme */
  theme: Theme;
}

export const useFormatCategoricalData = ({
  data,
  caping,
  sortingMethod,
  others,
  colorTheme,
  theme,
}: UseFormatCategoricalDataProps) => {
  const computedData = useMemo(() => {
    let computedData;

    if (data === undefined || data === null) {
      return undefined;
    }

    computedData = [...data!].sort(getSortingMethod(sortingMethod));

    if (caping) {
      if (typeof others === 'boolean' && !others) {
        computedData = computedData.slice(0, caping);
      } else {
        computedData = computedData.reduce<CategoryData>((acc, curr, index) => {
          if (index < caping) {
            acc[index] = curr;
          } else if (index === caping) {
            acc[index] = {
              key: typeof others === 'string' ? others : 'Others',
              value: curr.value,
            };
          } else {
            acc[caping] = {
              ...acc[caping],
              value: acc[caping].value + curr.value,
            };
          }
          return acc;
        }, []);
      }
    }

    return computedData;
  }, [data, caping]);

  const colorScale = useColor({ data: computedData!, colorTheme, category: 'key', theme });

  return { data: computedData as CategoryData, colorScale };
};
